import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  FileCheck,
  Calendar,
  User,
  Download,
  Upload,
  ClipboardCheck,
  Users,
  CreditCard,
  Pause,
  Play,
} from "lucide-react";

import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Textarea } from "@shared/ui/textarea";
import { Input } from "@shared/ui/input";

import { useToast } from "@shared/hooks/use-toast";
import MainLayout from "./MainLayout";
import WorkflowProgress from "./WorkflowProgress";
import ApplicationHeader from "./ApplicationHeader";
import DocumentItem from "./DocumentItem";
import { useWorkflow } from "./WorkflowContext";
import {
  useUpdateConsumerStatus,
  useCreateConsumerNote,
  useUpdateStatus,
  useConsumerDetails,
  useGetNotes,
} from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  SERVICE_REQUEST_STATUS,
  SERVICE_REQUEST_STATUS_DISPLAY,
} from "@features/cx/shared/utils/constants";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";
import { Badge } from "@shared/ui/badge";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";

const ApplicationReview = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const updateConsumerStatus = useUpdateConsumerStatus();
  const createNoteMutation = useCreateConsumerNote();
  const {
    application,
    workflowSteps,
    verifyDocument,
    rejectDocument,
    moveToNextStep,
    addNote,
    updateApplicationStatus,
  } = useWorkflow();

  const [note, setNote] = useState("");
  const [noteName, setNoteName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { consumer_id, consumerId, id } = useParams<{
    consumer_id?: string;
    consumerId?: string;
    id?: string;
  }>();

  // Use the first available parameter (handles different URL patterns)
  const consumerIdFromUrl = consumer_id || consumerId || id;
  const updateStatus = useUpdateStatus(Number(consumerIdFromUrl));

  const {
    data: consumerData,
    isLoading,
    error,
  } = useConsumerDetails({
    remote_utility_id: remoteUtilityId,
    consumer_id: consumerIdFromUrl || "",
    is_detail_info: true,
  });

  const { data: getNotes } = useGetNotes({
    remote_utility_id: remoteUtilityId,
    request_id: consumerIdFromUrl || "",
  });

  const consumer = consumerData?.result;

  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("Service Connection Workflow");

  useEffect(() => {
    startTracking();
  }, [startTracking]);

  if (!application) {
    return <div>Loading...</div>;
  }

  const filteredDocuments = application.documents.filter((doc) =>
    doc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fixed document verification logic - use consumer?.document if available, fallback to application.documents
  const documentsToCheck = consumer?.document || application.documents;
  const allDocumentsVerified = documentsToCheck.every(
    (doc) => doc.status === "verified" || doc.statusDisplay === "Verified"
  );
  const hasRejectedDocuments = documentsToCheck.some(
    (doc) => doc.status === "rejected" || doc.statusDisplay === "rejected"
  );

  // Check if application is already accepted (case-insensitive)
  const isApplicationAccepted =
    consumer?.statusDisplay?.toLowerCase() === "accepted";

  // Updated handleAccept function
  const handleAccept = async () => {
    if (!allDocumentsVerified) {
      toast({
        title: "Cannot proceed",
        description: "All documents must be verified before proceeding.",
        variant: "destructive",
      });
      return;
    }

    if (consumerIdFromUrl) {
      try {
        // Wait for the API response
        const response = await updateConsumerStatus.mutateAsync(
          {
            consumerId: consumerIdFromUrl,
            status: "Accepted",
          },
          {
            onSuccess: () => {
              logTimeUntilStep("Application Review");
            },
          }
        );

        updateApplicationStatus("site_inspection");
        moveToNextStep();

        toast({
          title: "Application accepted",
          description:
            "The application has been accepted for site inspection scheduling.",
        });

        // Navigate with the API response data in state
        navigate(
          `/cx/consumer-onboarding/workflow/inspection/${consumerIdFromUrl}/ACCEPTED`,
          {
            state: {
              acceptanceData: response,
              fromAcceptance: true,
              acceptedAt: new Date().toISOString(),
              consumerId: consumerIdFromUrl,
            },
          }
        );
      } catch (error) {
        console.error("Error accepting application:", error);
        toast({
          title: "Error",
          description: "Failed to accept application. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleReject = () => {
    if (consumerIdFromUrl) {
      updateConsumerStatus.mutate({
        consumerId: consumerIdFromUrl,
        status: "Reject",
      });
    }

    updateApplicationStatus("rejected");
    toast({
      title: "Application rejected",
      description: "The application has been rejected.",
      variant: "destructive",
    });
  };

  // Updated handleAddNote function with API integration
  const handleAddNote = async () => {
    if (!note.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a note description.",
        variant: "destructive",
      });
      return;
    }

    if (!noteName.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a note name.",
        variant: "destructive",
      });
      return;
    }

    if (!consumerIdFromUrl) {
      toast({
        title: "Error",
        description: "Consumer ID not found.",
        variant: "destructive",
      });
      return;
    }

    try {
      await createNoteMutation.mutateAsync({
        consumer: consumerIdFromUrl,
        note_name: noteName.trim(),
        note_description: note.trim(),
        note_color: "bg-yellow-200",
      });

      // Update local state
      addNote(note);

      // Clear form
      setNote("");
      setNoteName("");

      toast({
        title: "Note added",
        description: "Your note has been added to the application.",
      });
    } catch (error) {
      console.error("Error adding note:", error);
      toast({
        title: "Error",
        description: "Failed to add note. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAcknowledge = () => {
    updateStatus.mutate({
      remote_utility_id: remoteUtilityId,
      progress_status: "IN_PROGRESS",
      acknowledge_date: new Date().toISOString(),
    });
    toast({
      title: "Application acknowledged",
      description:
        "The application has been acknowledged and is now in progress.",
    });
  };

  const handlePutOnHold = () => {
      try {
        updateStatus.mutate({
          remote_utility_id: remoteUtilityId,
          progress_status: "HOLD",
        });

        toast({
          title: "Application on hold",
          description: "The application has been put on hold.",
        });
      } catch (error) {
        console.error("Error putting application on hold:", error);
        toast({
          title: "Error",
          description: "Failed to put application on hold. Please try again.",
          variant: "destructive",
        });
      }
  };

  const handleResume = () => {
      try {
        updateStatus.mutate({
          remote_utility_id: remoteUtilityId,
          progress_status: "IN_PROGRESS",
        });

        toast({
          title: "Application resumed",
          description:
            "The application has been resumed and is now in progress.",
        });
      } catch (error) {
        console.error("Error resuming application:", error);
        toast({
          title: "Error",
          description: "Failed to resume application. Please try again.",
          variant: "destructive",
        });
      }
  };

  // Define action buttons
  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      condition: (status) =>
        application.progressStatusDisplay === "Acknowledge Pending",
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      condition: (status) =>
        application.progressStatusDisplay === "In Progress",
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      condition: (status) => application.progressStatusDisplay === "Hold",
    },
  ];

  const relevantActions = actionButtons.filter(
    (action) =>
      !action.condition || action.condition(application.progressStatusDisplay)
  );

  function getStatusClasses(status) {
    switch (status) {
      case "Acknowledge Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "In Progress":
        return "bg-green-100 text-green-800 border-green-300";
      case "Hold":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  }

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Service Connection Workflow{" "}
          <Badge
            className={getStatusClasses(application.progressStatusDisplay)}
          >
            {application.progressStatusDisplay}
          </Badge>
        </h1>
        <div className="flex gap-2">
          {relevantActions.map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant={action.variant}
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4 mr-2" />
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      <ApplicationHeader
        remote_utility_id={remoteUtilityId}
        is_detail_info={1}
      />

      <div className="mb-6">
        <WorkflowProgress
          steps={workflowSteps}
          currentStepId={application.currentStep}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="mb-6">
        <CardHeader className="bg-gray-50 border-b">
  <div className="flex justify-between items-center">
    {/* Left: Icon + Title + Subtitle */}
    <div>
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-workflow-blue" />
        <CardTitle>Required Documents</CardTitle>
      </div>
      <CardDescription className="ml-7">
        Verify each document before proceeding
      </CardDescription>
    </div>

    {/* Right: Document status counts */}
    <div className="flex items-center gap-4 text-sm">
      <div className="flex items-center">
        <CheckCircle className="h-4 w-4 text-workflow-green mr-1" />
        <span>
          {consumer?.document?.filter((d) => d.statusDisplay === "Verified").length} verified
        </span>
      </div>

      <div className="flex items-center">
        <XCircle className="h-4 w-4 text-workflow-red mr-1" />
        <span>
          {consumer?.document?.filter((d) => d.statusDisplay === "Rejected").length} rejected
        </span>
      </div>

      <div className="flex items-center">
        <AlertCircle className="h-4 w-4 text-workflow-orange mr-1" />
        <span>
          {consumer?.document?.filter((d) => d.statusDisplay === "Pending").length} pending
        </span>
      </div>
    </div>
  </div>
</CardHeader>

            <CardContent className="pt-4">
              <div className="space-y-2">
                {filteredDocuments.length > 0 ? (
                  filteredDocuments.map((doc) => {
                    // Find the matching documents from consumer?.document array
                    const matchingDocs =
                      consumer?.document?.filter(
                        (consumerDoc) => consumerDoc.id === doc.id
                      ) || [];

                    return (
                      <DocumentItem
                        key={doc.id}
                        name={doc.name}
                        consumerStatus={consumer?.statusDisplay}
                        doc={matchingDocs} // Pass only matching documents
                        uploadDate={doc.uploadDate}
                        fileUrl={doc.fileUrl}
                        documentId={doc.id}
                        onVerify={() => {
                          verifyDocument(doc.id);
                          toast({
                            title: "Document verified",
                            description: `${doc.name} has been verified.`,
                          });
                        }}
                        onReject={() => {
                          rejectDocument(doc.id);
                          toast({
                            title: "Document rejected",
                            description: `${doc.name} has been rejected.`,
                            variant: "destructive",
                          });
                        }}
                        onView={() => {
                          toast({
                            title: "Document viewer",
                            description: "Document viewer would open here.",
                          });
                        }}
                      />
                    );
                  })
                ) : (
                  <p className="text-center py-4 text-gray-500">
                    No documents match your search
                  </p>
                )}
              </div>
            </CardContent>
            <CardFooter className="border-t bg-gray-50 flex justify-between">
              <div className="text-sm text-gray-500">
                Showing {filteredDocuments.length} of{" "}
                {consumer?.document?.length} documents
              </div>
            </CardFooter>
          </Card>

          <div className="mt-6">
            <Card>
              <CardHeader className="bg-gray-50 border-b">
                <div className="flex justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-workflow-purple" />
                    Application Notes
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleAddNote}
                    disabled={
                      !note.trim() ||
                      !noteName.trim() ||
                      createNoteMutation.isPending
                    }
                  >
                    {createNoteMutation.isPending ? "Adding..." : "Add Note"}
                  </Button>
                </div>
                <CardDescription>
                  Team communication and important notes
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div className="mb-4 space-y-3">
                    <Input
                      placeholder="Enter note name..."
                      value={noteName}
                      onChange={(e) => setNoteName(e.target.value)}
                      className="w-full"
                    />
                    <Textarea
                      placeholder="Add a note about this application..."
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      className="min-h-[100px]"
                    />
                  </div>

                  {getNotes?.result?.length > 0 ? (
                    getNotes?.result?.map((note) => (
                      <div
                        key={note.id}
                        className="bg-gray-50 p-3 rounded-md border"
                      >
                        <div className="flex justify-between">
                          <span className="font-medium text-workflow-blue">
                            {note.createdUserRemoteName}
                          </span>
                          <span className="text-sm text-gray-500">
                            {note.createdDate}
                          </span>
                        </div>
                        <div className="mt-2 flex gap-2">
                          <p className="font-semibold">Name:</p>
                          <p className="">{note.noteName}</p>
                        </div>
                        <div className="mt-2 flex gap-2">
                          <p className="font-semibold">Description:</p>
                          <p className="">{note.noteDescription}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      No notes have been added yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div>
          <Card className="bg-white border-workflow-blue border-t-4 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl font-bold">
                Application Review
              </CardTitle>
              <CardDescription>
                Review application details and make a decision
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                  <h3 className="font-medium flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-workflow-blue" />
                    Requirements Checklist
                  </h3>
                  <ul className="mt-2 space-y-2">
                    <li className="flex items-center gap-2">
                      {allDocumentsVerified ? (
                        <CheckCircle className="h-4 w-4 text-workflow-green" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-workflow-orange" />
                      )}
                      <span className="text-sm">All documents verified</span>
                    </li>
                    <li className="flex items-center gap-2">
                      {!hasRejectedDocuments ? (
                        <CheckCircle className="h-4 w-4 text-workflow-green" />
                      ) : (
                        <XCircle className="h-4 w-4 text-workflow-red" />
                      )}
                      <span className="text-sm">No rejected documents</span>
                    </li>
                  </ul>
                </div>

                {/* Payment Details Section */}
                <div className="p-3 rounded-md bg-green-50 border border-green-200">
                  <h3 className="font-medium flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-workflow-green" />
                    Payment Details
                  </h3>
                  <div className="mt-2 space-y-2">
                    <div className="grid grid-cols-2">
                      <span className="text-sm font-medium">Amount:</span>
                      <span className="text-sm">
                        ${application.payment?.amount || "N/A"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2">
                      <span className="text-sm font-medium">Payment Type:</span>
                      <span className="text-sm">
                        {application.payment?.paymentPayTypeDisplay || "N/A"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2">
                      <span className="text-sm font-medium">Payment Mode:</span>
                      <span className="text-sm">
                        {application.payment?.paymentModeDisplay || "N/A"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2">
                      <span className="text-sm font-medium">
                        Payment Status:
                      </span>
                      <span className="text-sm">
                        {application.payment?.status || "N/A"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2">
                      <span className="text-sm font-medium">Payment Date:</span>
                      <span className="text-sm">
                        {application.payment?.paymentDate || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-2">
                  <Button
                    className="w-full bg-green-600 hover:bg-green-600"
                    onClick={handleAccept}
                    disabled={
                      !allDocumentsVerified ||
                      hasRejectedDocuments ||
                      isApplicationAccepted ||
                      updateConsumerStatus.isPending ||
                      consumer?.statusDisplay?.toLowerCase() !== "pending" ||
                      application.progressStatusDisplay ===
                        "Acknowledge Pending"
                    }
                  >
                    {updateConsumerStatus.isPending
                      ? "Processing..."
                      : "Accept Application"}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full text-workflow-red border-workflow-red hover:bg-red-50"
                    onClick={handleReject}
                    disabled={
                      isApplicationAccepted ||
                      consumer?.statusDisplay?.toLowerCase() !== "pending" ||
                      application.progressStatusDisplay ===
                        "Acknowledge Pending"
                    }
                  >
                    Reject Application
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ApplicationReview;
